import React from 'react';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { defaultStyles, FileIcon } from 'react-file-icon';
import { connect } from 'react-redux';
import classnames from 'classnames';
import Compressor from 'compressorjs';
import ExifReader from 'exifreader';
import moment from 'moment';

import Button, { CameraButton, DeleteButton } from '@components/Button';
import Input from '@components/Input';
import ModalBase from '@components/ModalBase';

import { getLocation } from '@actions/gis';
import { getPresignedURL, S3_SIGNED_URL_SUCCESS } from '@actions/s3';

import { getEmptyErrorState, getIndexedFileName, humanFileSize, MAX_ATTACHMENT_FILE_SIZE } from '@utils/AppUtil';
import {
  defaultAttachmentTagId,
  hasRegularUnits,
  IAttachmentTagContext,
  isTaggableImageFile,
} from '@utils/attachmentTag';
import CyberdyneValidator from '@utils/cyberdyneValidator';
import { getFormattedDeviceDetails } from '@utils/DeviceDetailsUtil';
import ErrorMessageGenerator from '@utils/ErrorMessageGenerator';
import FileUtil from '@utils/FileUtil';
import { track } from '@utils/MixPanelUtils';
import { s3uploadFile } from '@utils/S3Util';

import AttachmentImage from './AttachmentImage';
import AttachmentTagField from './AttachmentTagField';

import './style.scss';

interface IFileUpload {
  details: File;
  data: string;
  progress: number;
  title: string;
  description: string;
  createdAt?: {
    isDisabled: boolean;
    value: Date;
  };
  location?: {
    isDisabled: boolean;
    value: string;
  };
  s3SavedUrl: string;
  presignedUrl?: string;
  error: {
    title: IFieldValidation;
    description: IFieldValidation;
    file: IFieldValidation;
  };
}

const cyberdyneValidator = new CyberdyneValidator();
const errMsgGen = new ErrorMessageGenerator();

export interface IAttachmentsModalProps {
  show: boolean;
  s3Operation?: S3Operation;
  onAdd?: (files: IAttachment[]) => void;
  onClose?: (e: any) => void;
  attachments?: IAttachment[];
  acceptedFileTypes?: string[];
  maxFileSize?: number;
  title: string;
  agencyId: number;
  getSignedURL: TGetSignedURL;
  getPresignedURL: (fileUrl: string, expireIn?: number, bucket?: string) => Promise<any>;
  addError?: TAddError;
  trackLabel?: string;
  hasEditableFields?: boolean;
  noAttachmentsMessage?: string;
  convertImageSizeAfter?: number;
  getLocation: TGetLocation;
  tagContext?: IAttachmentTagContext;
}

interface IAttachmentsModalState {
  files: Map<string, IFileUpload>;
  prevShow: boolean;
  isConfirmModalOpen?: boolean;
  locationUnitId: number | null;
}

export const ACCEPTED_FILE_TYPES = [
  '.ai',
  '.aiff',
  '.bmp',
  '.doc',
  '.docx',
  '.eml',
  '.eps',
  '.gif',
  '.htm',
  '.html',
  '.jpe',
  '.jpeg',
  '.jpg',
  '.mp3',
  '.mp4',
  '.msg',
  '.pdf',
  '.png',
  '.ppt',
  '.pptx',
  '.ps',
  '.rtf',
  '.tif',
  '.tiff',
  '.txt',
  '.vcf',
  '.wav',
  '.xls',
  '.xlsx',
  '.zip',
  '.mov',
  '.heic',
  '.heif',
];

export class AttachmentsModal extends React.Component<IAttachmentsModalProps, IAttachmentsModalState> {
  static defaultProps: Partial<IAttachmentsModalProps> = {
    acceptedFileTypes: ACCEPTED_FILE_TYPES,
    maxFileSize: MAX_ATTACHMENT_FILE_SIZE,
    s3Operation: S3Operation.CASE_ATTACHMENT,
    hasEditableFields: true,
  };

  constructor(props: IAttachmentsModalProps) {
    super(props);
    this.state = {
      files: new Map(),
      prevShow: false,
      locationUnitId: null,
    };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.show) {
      if (!prevState.prevShow) {
        return {
          files: new Map(),
          prevShow: true,
          locationUnitId: defaultAttachmentTagId(nextProps.tagContext),
        };
      }
    } else if (prevState.prevShow) {
      return {
        prevShow: false,
      };
    }

    return null;
  }

  getAcceptedFileTypesString = (): string =>
    this.props.acceptedFileTypes ? this.props.acceptedFileTypes.join(', ') : '';

  onDragEnter = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  onDragExit = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  onDragLeave = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  onDragOver = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.handleAttachmentChange(e);
  };

  getFileEmptyErrorState = () => ({
    title: getEmptyErrorState(),
    description: getEmptyErrorState(),
    file: getEmptyErrorState(),
  });

  getThumb = (file: IFileUpload) => {
    const fileNameExtension = file.details && file.details.name.split('.').pop().toLowerCase();

    const fileType = /(jpe?g|png)$/.test(fileNameExtension) ? 'image' : 'others';
    const fileTypeToThumb = {
      image: (
        <AttachmentImage
          className={classnames('attachments-modal__saved-thumb')}
          src={file.data}
          alt={file.details?.name || 'Attachment preview'}
        />
      ),
      others: (
        <div className={classnames('attachments-modal__saved-thumb')}>
          <FileIcon extension={fileNameExtension} {...defaultStyles[fileNameExtension]} />
        </div>
      ),
    };

    return fileTypeToThumb[fileType];
  };

  getValidFiles = (files: FileList) => {
    let validFiles = [];
    let hasInvalidFiles = false;

    for (const index in files) {
      if (!isNaN(+index)) {
        const splitText = files[index].name.split('.');
        const fileType = `.${splitText[splitText.length - 1]}`;

        if (this.props.acceptedFileTypes.includes(fileType.toLowerCase())) {
          validFiles = validFiles.concat(files[index]);
        } else {
          hasInvalidFiles = true;
        }
      }
    }

    if (hasInvalidFiles && this.props.addError) {
      this.props.addError(errMsgGen.getUnsupportedFileFormatMessage());
    }

    return validFiles;
  };
  // TODO: Use async await to make it readable
  handleAttachmentChange = async (e) => {
    let files = null;
    const imageConvertTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/jpe'];

    if (e.dataTransfer) {
      files = this.getValidFiles(e.dataTransfer.files);
    } else if (e.target) {
      files = this.getValidFiles(e.target.files);
    }

    try {
      files = await Promise.all(files.map(FileUtil.convertHeicToJpeg));
    } catch {
      if (this.props.addError) {
        this.props.addError('Could not convert HEIC/HEIF file. Please convert to JPEG and try again.');
      }

      return;
    }

    const isImage = (file) => {
      const split = file.name.split('.');
      const extension = split[split.length - 1];

      return /png|jpe?g/.test(extension.toLowerCase());
    };

    const onUploadProgress = (progressEvent, key) => {
      const progress: number = (progressEvent.loaded / progressEvent.total) * 100;

      const newFiles = new Map(this.state.files);

      if (newFiles.get(key)) {
        this.setState((prevState) => ({
          files: prevState.files.set(key, { ...prevState.files.get(key), progress }),
        }));
      }
    };

    const onUploadComplete = async (response, key) => {
      const newFiles = new Map(this.state.files);

      // TODO: Change this to use XMLParser?
      if (newFiles.get(key) && response.documentElement && response.documentElement.getElementsByTagName('Location')) {
        const currentFile = newFiles.get(key);

        const s3SavedUrl = decodeURIComponent(
          response.documentElement.getElementsByTagName('Location')[0].childNodes[0].data
        );

        const presignedUrl = await this.props.getPresignedURL(s3SavedUrl);

        this.setState((prevState) => ({
          files: prevState.files.set(key, {
            ...prevState.files.get(key),
            s3SavedUrl,
            title: currentFile.title.trim().length ? currentFile.title : key,
            presignedUrl: presignedUrl?.response,
          }),
        }));
      }
    };

    const onUploadFailed = (error, key) => {
      const newFiles = new Map(this.state.files);

      // TODO: Change this to use XMLParser?
      if (newFiles.get(key)) {
        const currentFile = newFiles.get(key);

        newFiles.set(key, {
          ...currentFile,
          progress: 100,
          s3SavedUrl: null,
          error: {
            ...currentFile.error,
            file: {
              errorState: IValidationState.ERROR,
              errorMessage: 'The upload was not successful, please try again.',
            },
          },
        });

        this.setState({
          files: newFiles,
        });
      }
    };

    const updatedFiles = new Map(this.state.files);
    files.map((file) => {
      const reader = new FileReader();
      const fileKey = getIndexedFileName(file.name);

      reader.onloadend = async () => {
        updatedFiles.set(fileKey, {
          data: reader.result as string,
          details: file,
          progress: 0,
          title: '',
          description: '',
          s3SavedUrl: '',
          error: this.getFileEmptyErrorState(),
          createdAt: {
            isDisabled: false,
            value: null,
          },
        });
        this.setState({
          files: updatedFiles,
        });
        let tags;
        if (isImage(file)) {
          tags = await ExifReader.load(file, { expanded: true });
        }
        if (tags && tags.gps && tags.gps.Latitude && tags.gps.Longitude) {
          const coords = {
            lat: tags.gps.Latitude,
            long: tags.gps.Longitude,
          };
          this.props
            .getLocation(coords)
            .then((response) => {
              if (response.response) {
                updatedFiles.set(fileKey, {
                  ...updatedFiles.get(fileKey),
                  createdAt: {
                    isDisabled: !!(tags.DateTime && tags.DateTime.value.length),
                    value: !!(tags.DateTime && tags.DateTime.value.length)
                      ? moment(tags.DateTime.value[0], 'YYYY:MM:DD hh:mm:ss').toDate()
                      : null,
                  },
                  location: {
                    isDisabled: true,
                    value: response.response,
                  },
                });
              }
              this.setState({
                files: updatedFiles,
              });
            })
            .catch((err) => {
              this.setState({
                files: updatedFiles,
              });
            });
        }
        this.props.getSignedURL(this.props.agencyId, this.props.s3Operation, fileKey).then((action) => {
          if (action.type === S3_SIGNED_URL_SUCCESS) {
            const body = new Map(action.response.requestBodyparams);
            if (isImage(file)) {
              // tslint:disable-next-line:no-unused-expression
              new Compressor(file, {
                quality: 0.6,
                convertTypes: imageConvertTypes,
                convertSize: this.props.convertImageSizeAfter || Infinity,
                success(compressedFile: Blob) {
                  s3uploadFile(body, compressedFile, (ev) => onUploadProgress(ev, fileKey)).then(
                    (response) => {
                      onUploadComplete(response, fileKey);
                    },
                    (error) => {
                      onUploadFailed(error, fileKey);
                    }
                  );
                },
              });
            } else {
              s3uploadFile(body, file, (ev) => onUploadProgress(ev, fileKey)).then(
                (response) => {
                  onUploadComplete(response, fileKey);
                },
                (error) => {
                  onUploadFailed(error, fileKey);
                }
              );
            }
          } else {
            onUploadFailed(action.error, fileKey);
          }
        });
      };

      if (file.size < this.props.maxFileSize) {
        if (isImage(file)) {
          // tslint:disable-next-line:no-unused-expression
          new Compressor(file, {
            quality: 0.7,
            convertTypes: imageConvertTypes,
            convertSize: this.props.convertImageSizeAfter || Infinity,
            success: (compressedFile) => {
              reader.readAsDataURL(compressedFile);
            },
          });
        } else {
          reader.readAsDataURL(file);
        }
      } else {
        if (this.props.addError) {
          const maxFileSizeInMB = this.props.maxFileSize / (1024 * 1024);
          this.props.addError(errMsgGen.getMaxFileSizeLimitMessage(maxFileSizeInMB));
        }
      }
    });
    e.target.value = null;
  };

  isAddDisabled = () => {
    const files = this.state.files;
    if (files.size === 0) {
      return true;
    }

    for (const file of Array.from(files.values())) {
      if (file.progress !== 100 || !file.s3SavedUrl) {
        return true;
      }
    }

    return Array.from(files.keys()).some(
      (fileKey) => files.get(fileKey) && files.get(fileKey).error.file.errorState === IValidationState.ERROR
    );
  };

  onConfirmClose = (e) => {
    this.setState({
      isConfirmModalOpen: false,
      files: new Map(),
    });

    this.props.onClose(e);
  };

  onNotConfirmClose = () => {
    this.setState({
      isConfirmModalOpen: false,
    });
  };

  onClose = (e) => {
    if (this.state.files.size > 0) {
      this.setState({
        isConfirmModalOpen: true,
      });
    } else {
      this.props.onClose(e);
    }
  };

  isValidFile = (file: IFileUpload, fileName: string) => {
    const { description, title } = file;
    const error = this.getFileEmptyErrorState();
    let isValid = true;

    error.file = file.error.file;

    if (error.file.errorState === IValidationState.ERROR) {
      isValid = false;
    }

    if (cyberdyneValidator.isStringGreaterThanLength(description, 300)) {
      error.description.errorState = IValidationState.ERROR;
      error.description.errorMessage = errMsgGen.getCharacterLimitMessage('Description', 300);

      isValid = false;
    }

    if (this.props.hasEditableFields && cyberdyneValidator.isEmptyString(title)) {
      error.title.errorState = IValidationState.ERROR;
      error.title.errorMessage = errMsgGen.getMandatoryFieldMessage('Name');

      isValid = false;
    } else if (cyberdyneValidator.isStringGreaterThanLength(title, 200)) {
      error.title.errorState = IValidationState.ERROR;
      error.title.errorMessage = errMsgGen.getCharacterLimitMessage('Name', 200);

      isValid = false;
    }

    if (this.props.addError && cyberdyneValidator.isStringGreaterThanLength(fileName, 200)) {
      // CYBERD-2628 fix
      this.props.addError(errMsgGen.getFileNameCharacterLimitMessage(200));
      isValid = false;
    }

    return { isValid, error };
  };

  isValid = () => {
    let isValid = true;
    const files = new Map(this.state.files);

    Array.from(files.keys()).map((fileKey) => {
      const file = files.get(fileKey);
      const isFileValid = this.isValidFile(file, fileKey);

      files.set(fileKey, {
        ...file,
        error: isFileValid.error,
      });

      isValid = isValid && isFileValid.isValid;
    });

    this.setState({
      files,
    });

    return isValid;
  };

  onAdd = () => {
    if (this.isAddDisabled() || !this.isValid()) {
      return;
    }

    const files = this.state.files;

    const tagImages = this.isTagControlVisible() && typeof this.state.locationUnitId === 'number';
    const caseAttachmentFiles: IAttachment[] = Array.from(files.keys()).map((fileKey) => {
      const file = files.get(fileKey);
      const attachment = {
        title: file.title.trim(),
        description: file.description && file.description.trim() ? file.description.trim() : null,
        fileName: fileKey,
        fileSize: humanFileSize(file.details.size),
        fileURL: file.s3SavedUrl,
        presignedUrl: file.presignedUrl,
        data: file.data,
        exif: {
          location: (file.location && file.location.value) || null,
          createdAt: (file.createdAt && file.createdAt.value) || null,
        },
      } as IAttachment;

      if (tagImages && this.isTaggableUpload(file)) {
        attachment.locationUnitId = this.state.locationUnitId;
      }

      return attachment;
    });

    this.props.onAdd(caseAttachmentFiles);
  };

  removeFile = (fileKey: string) => {
    const currentFiles = this.state.files;
    const newFiles = new Map(currentFiles);
    newFiles.delete(fileKey);

    this.setState({
      files: newFiles,
    });
  };

  isTaggableUpload = (file: IFileUpload) =>
    isTaggableImageFile({
      mimeType: file.details && file.details.type,
      fileName: file.details && file.details.name,
    });

  isTagControlVisible = () =>
    !!this.props.tagContext &&
    hasRegularUnits(this.props.tagContext.units) &&
    Array.from(this.state.files.values()).some((file) => this.isTaggableUpload(file));

  onLocationUnitChange = (locationUnitId: number | null) => {
    this.setState({ locationUnitId });
  };

  onNameDescriptionChange = (e, key) => {
    const newFiles = new Map(this.state.files);
    const targeName = e.target.name;

    if (targeName === 'createdAt' || targeName === 'location') {
      newFiles.set(key, {
        ...newFiles.get(key),
        [targeName]: {
          isDisabled: false,
          value: e.target.value || null,
        },
      });
    } else {
      newFiles.set(key, {
        ...newFiles.get(key),
        [targeName]: e.target.value,
      });
    }

    this.setState({
      files: newFiles,
    });
  };

  trackName = () => {
    track(`${this.props.trackLabel}_ADD_ATTACHMENT_PHOTO_NAME_UPDATED`, {
      'Device Details': getFormattedDeviceDetails(),
    });
  };

  trackDescription = () => {
    track(`${this.props.trackLabel}_ADD_ATTACHMENT_PHOTO_DESCRIPTION_UPDATED`, {
      'Device Details': getFormattedDeviceDetails(),
    });
  };

  getContent = () => {
    const { maxFileSize } = this.props;
    const maxFileSizeText = maxFileSize / (1024 * 1024);

    return (
      <div className={classnames('attachments-modal__content')}>
        <div className={classnames('attachments-modal__add')}>
          <div
            onDragEnter={this.onDragEnter}
            onDragExit={this.onDragExit}
            onDragLeave={this.onDragLeave}
            onDragOver={this.onDragOver}
            onDrop={this.onDrop}
            className={classnames('attachments-modal__drag-drop-box')}
          >
            <span />
            <span>Drag & Drop or </span>
            <label htmlFor="file-upload">Browse file</label>
            <input
              id="file-upload"
              type="file"
              accept={this.getAcceptedFileTypesString()}
              onChange={(e) => this.handleAttachmentChange(e)}
              multiple={true}
            />
          </div>
          <div className={classnames('attachments-modal__or')}>OR</div>
          <CameraButton id="camera" onClick={this.handleAttachmentChange} accept={this.getAcceptedFileTypesString()}>
            <label className={classnames('content-placeholder')} htmlFor="camera">
              <span />
              <span>Camera</span>
            </label>
          </CameraButton>
        </div>
        <div className={classnames('attachments-modal__disclaimer')}>
          <span className={classnames('attachments-modal__disclaimer-icon')} />
          <div className={classnames('attachments-modal__disclaimer-msg')}>
            <span>Maximum file size (each file) is {maxFileSizeText}MB</span>
            <span>{`Accepted file types are ${this.getAcceptedFileTypesString()}.`}</span>
          </div>
        </div>
        <div className={classnames('attachments-modal__saved-list')}>
          {this.isTagControlVisible() && this.props.tagContext && (
            <AttachmentTagField
              context={this.props.tagContext}
              value={this.state.locationUnitId}
              onChange={this.onLocationUnitChange}
            />
          )}
          {this.state.files.size > 0 ? (
            Array.from(this.state.files.keys()).map((fileKey, index, map) => {
              const file = this.state.files.get(fileKey);
              const error = file.error;

              return file.details ? (
                <div key={index} className={classnames('attachments-modal__saved')}>
                  {this.getThumb(file)}
                  <div className={classnames('attachments-modal__saved-details')}>
                    <div className={classnames('attachments-modal__saved-progress')}>
                      <div className={classnames('attachments-modal__saved-title')}>
                        <span>
                          <b>{fileKey}</b>
                          {`(${humanFileSize(file.details.size)})`}
                        </span>
                        <Input
                          field={{
                            label: ' ',
                            value: null,
                            isRequired: false,
                            type: null,
                          }}
                          className={classnames('attachments-modal__progress')}
                          name=" "
                          width={12}
                          onChange={() => null}
                          error={error.file}
                          renderField={() => (
                            <ProgressBar
                              now={file.progress}
                              variant={
                                error.file.errorState === IValidationState.ERROR
                                  ? ButtonStyle.DANGER
                                  : ButtonStyle.SUCCESS
                              }
                            />
                          )}
                        />
                      </div>
                      <DeleteButton onClick={() => this.removeFile(fileKey)} />
                    </div>
                    {this.props.hasEditableFields && (
                      <>
                        <Input
                          field={{
                            label: 'Name',
                            value: file.title,
                            isRequired: true,
                            type: CustomFieldType.TEXT,
                          }}
                          placeholder="Attachment name here"
                          name="title"
                          onFocus={this.props.trackLabel ? this.trackName : undefined}
                          onChange={(e) => this.onNameDescriptionChange(e, fileKey)}
                          width={12}
                          error={error.title}
                        />
                        <Input
                          field={{
                            label: 'Description',
                            value: file.description,
                            isRequired: false,
                            type: CustomFieldType.TEXT,
                          }}
                          placeholder="Attachment description here"
                          name="description"
                          onFocus={this.props.trackLabel ? this.trackDescription : undefined}
                          onChange={(e) => this.onNameDescriptionChange(e, fileKey)}
                          width={12}
                          error={error.description}
                        />
                        <Input
                          field={{
                            label: 'Created On',
                            value: file.createdAt && file.createdAt.value,
                            isRequired: false,
                            type: CustomFieldType.DATE,
                          }}
                          datePickerOptions={{
                            dateFormat: 'MM/DD/YY',
                            timeFormat: 'HH:mm:ss',
                          }}
                          disabled={file.createdAt && file.createdAt.isDisabled}
                          placeholder="MM/DD/YY HH:mm:ss"
                          name="createdAt"
                          onFocus={this.props.trackLabel ? track : undefined}
                          onChange={(e) => this.onNameDescriptionChange(e, fileKey)}
                          width={6}
                        />
                        <Input
                          field={{
                            label: 'Location',
                            value: file.location && file.location.value,
                            isRequired: false,
                            type: CustomFieldType.TEXT,
                          }}
                          disabled={file.location && file.location.isDisabled}
                          placeholder="Attachment location here"
                          name="location"
                          onFocus={this.props.trackLabel ? track : undefined}
                          onChange={(e) => this.onNameDescriptionChange(e, fileKey)}
                          width={6}
                        />
                      </>
                    )}
                  </div>
                </div>
              ) : null;
            })
          ) : (
            <div className={classnames('attachments-modal__saved-list', 'attachments-modal__saved-list--empty')}>
              {this.props.noAttachmentsMessage || 'No attachments uploaded'}
            </div>
          )}
        </div>
      </div>
    );
  };

  getFooter = () => (
    <div className={classnames('attachments-modal__footer')}>
      <Button bsStyle={ButtonStyle.DEFAULT} onClick={this.onClose} content="Cancel" />
      <Button
        bsStyle={ButtonStyle.PRIMARY}
        onClick={this.onAdd}
        content="Add"
        disabled={this.isAddDisabled()}
        showLoader={this.isAddDisabled()}
      />
    </div>
  );

  render() {
    const title = `Add ${this.props.title}`;

    return (
      <div>
        <ModalBase
          show={!this.state.isConfirmModalOpen && this.props.show}
          onClose={(e) => this.onClose(e)}
          closeButton={true}
          titleElement={title}
          bodyElement={this.getContent()}
          footerElement={this.getFooter()}
          className={classnames('attachments-modal')}
        />
        <ModalBase
          show={this.state.isConfirmModalOpen}
          onClose={this.onNotConfirmClose}
          titleElement="Close Add Attachments"
          bodyElement="Do you want to close Add Attachments?"
          footerElement={
            <div className={classnames('attachments-modal__footer')}>
              <Button bsStyle={ButtonStyle.PRIMARY} onClick={this.onConfirmClose} content="Yes, Close" />
              <Button bsStyle={ButtonStyle.DEFAULT} onClick={this.onNotConfirmClose} content="No, Don't Close" />
            </div>
          }
          className={classnames('confirm-modal')}
        />
      </div>
    );
  }
}
const mapStateToProps = (state: IReduxStore) => ({});
const mapDispatchToProps = (dispatch: any) => ({
  getLocation: (parcelDetails: ILocationCoordinatesRequest) => dispatch(getLocation(parcelDetails)),
  getPresignedURL: (fileUrl: string, expireIn?: number, bucket?: string) =>
    dispatch(getPresignedURL(fileUrl, expireIn, bucket)),
});
export default connect(mapStateToProps, mapDispatchToProps)(AttachmentsModal);
